import * as Sentry from "@sentry/react"
import { QueryErrorResetBoundary } from "@tanstack/react-query"
import { ErrorInfo, ReactNode } from "react"
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary"
import { NotFoundError } from "routes/auth/404"
import { getHttpErrorInfo } from "utils/error-handling"
import { isSentryEnabled } from "utils/isSentryEnabled"

import { ErrorFallback } from "./ErrorFallback"

export interface ErrorBoundaryProps {
  children?: ReactNode
}

/**
 * ErrorBoundary is a higher-order component that acts as a wrapper to handle errors in the component tree.
 * It captures JavaScript errors in the children components, logs the errors, and renders a fallback UI.
 *
 * This component catches various types of errors, including
 * - React errors
 * - Query errors
 *
 * This component will NOT catch async errors. These needs to be handled in a separate way
 * with `interceptErrors()` or `proceedWithErrors()` functions.
 *
 * The ErrorBoundary leverages Sentry for error tracking and reporting, and it supports automatic UI recovery
 * through a reset mechanism.
 *
 * Error handling includes:
 * - Logging captured errors to Sentry if the integration is enabled.
 * - Providing a fallback UI rendered when an error is encountered.
 * - Resetting the error boundary state when a reset action is triggered.
 *
 * Props:
 * - children: ReactNode - Components wrapped by the ErrorBoundary.
 *
 * Internally, the component uses:
 * - ReactErrorBoundary to handle error boundaries in React.
 * - QueryErrorResetBoundary for managing query-related state resets.
 * - ErrorFallback to render fallback content when an error occurs.
 */
export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    // output error to console to make sure we see it
    console.error(error)

    if (isSentryEnabled()) {
      // Report to Sentry
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      })
    }
  }

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ReactErrorBoundary
          onReset={reset}
          onError={handleError}
          fallbackRender={(props) => {
            // eslint-disable-next-line react/prop-types
            const errorInfo = getHttpErrorInfo(props.error)
            if (errorInfo.status === 404) {
              return <NotFoundError />
            }
            return <ErrorFallback {...props} />
          }}
        >
          {children}
        </ReactErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}
